import { Component } from 'react'
import type { ReactNode, ErrorInfo } from 'react'
import { captureHandledError } from '../lib/sentry'
import { useLang } from '../contexts/LangContext'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

// Class components can't call hooks directly, so the translated fallback UI
// lives in this small functional component instead. useLang() throws outside
// LangProvider, so <ErrorBoundary> must stay nested under <LangProvider> in
// App.tsx — otherwise this last-resort fallback would itself crash uncaught.
function ErrorBoundaryFallback({ error }: { error: Error | null }) {
  const { t } = useLang()
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-8"
      style={{ backgroundColor: 'var(--bg-page)' }}
    >
      <div
        className="rounded-2xl p-8 max-w-md w-full text-center"
        style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)' }}
      >
        <div className="text-4xl mb-4">⚠️</div>
        <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
          {t('errorBoundary.title')}
        </h2>
        <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
          {t('errorBoundary.message')}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white"
          style={{ backgroundColor: 'var(--brand)' }}
        >
          {t('errorBoundary.reload')}
        </button>
        {import.meta.env.DEV && error && (
          <pre className="mt-4 text-left text-xs p-3 rounded overflow-auto max-h-32"
            style={{ backgroundColor: 'var(--bg-subtle)', color: 'var(--text-muted)' }}>
            {error.message}
          </pre>
        )}
      </div>
    </div>
  )
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack)
    // Forward to Sentry with the React component stack so we can pinpoint
    // which subtree blew up. No-op when Sentry isn't initialized (dev / no DSN).
    captureHandledError(error, {
      source:         'ErrorBoundary',
      componentStack: info.componentStack,
    })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback
      return <ErrorBoundaryFallback error={this.state.error} />
    }
    return this.props.children
  }
}
